import { normalizeClinicalTimestamp, clinicalInstant, clinicalWallTime, clinicalSortValue, clinicalTimeCandidates, clinicalOffset, captureClinicalNow, clinicalTimeLabel } from './clinicalTime';
import { isCriticalTimeUnknown } from './unknownCriticalTimes';
import { getLaborRecordChartDateTime, getLaborRecordOccurrence } from './laborRecordTimeUtils';

const padSeconds = (time) => (time && time.length === 5 ? `${time}:00` : time);

const BEFORE_BODY_BIRTH_FIELDS = new Set([
  'pushingStart',
  'crowning',
  'headBornTime'
]);

const AFTER_BODY_BIRTH_FIELDS = new Set([
  'firstBreathTime'
]);

const addDaysToDatePart = (datePart, days) => {
  const [year, month, day] = String(datePart || '').split('-').map(Number);
  if (!year || !month || !day) return datePart;
  const date = new Date(year, month - 1, day + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const timeToSeconds = (value) => {
  const [hours, minutes, seconds = '0'] = String(value || '').split(':').map(Number);
  if (![hours, minutes, seconds].every(Number.isFinite)) return null;
  return (hours * 3600) + (minutes * 60) + seconds;
};

export const toLocalDateTime = normalizeClinicalTimestamp;

export const splitDT = (value, fallbackDate) => {
  if (!value) return { date: '', time: '' };

  if (value instanceof Date && !isNaN(value)) {
    const date = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    const time = `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}:${String(value.getSeconds()).padStart(2, '0')}`;
    return { date, time, timestamp: captureClinicalNow(undefined, value) };
  }

  const stringValue = typeof value === 'string' ? value.trim() : '';
  if (!stringValue) return { date: '', time: '' };

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(stringValue)) {
    const date = typeof fallbackDate === 'string' ? fallbackDate.split('T')[0] : '';
    return { date, time: padSeconds(stringValue) };
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return { date: stringValue, time: '' };
  }

  const dateTime = toLocalDateTime(stringValue);
  if (!dateTime) return { date: '', time: '' };

  return {
    date: dateTime.slice(0, 10),
    time: padSeconds(clinicalWallTime(dateTime).slice(11, 19)),
    ...(clinicalOffset(dateTime) !== null ? { timestamp: dateTime } : {})
  };
};

// Labor episode birth fields are stored as charted clock times without an offset.
// Use the same interpretation on reload as on entry and save, including the
// Baby.BirthDate fallback when no Body Born time has been charted.
export const getChartedBabyBirthParts = (baby = {}, birthEvent = {}) => {
  const criticalTimes = birthEvent.criticalTimes || birthEvent.CriticalTimes || {};
  const bodyBorn = birthEvent.bodyBornTime || birthEvent.BodyBornTime ||
    criticalTimes.bodyBornTime || criticalTimes.BodyBornTime;
  const bodyParts = splitDT(bodyBorn);
  if (bodyParts.date && bodyParts.time) {
    return { dateOfBirth: bodyParts.date, timeOfBirth: bodyParts.time };
  }
  const dateOfBirth = baby.dateOfBirth || baby.DateOfBirth;
  const timeOfBirth = baby.timeOfBirth || baby.TimeOfBirth;
  if (dateOfBirth) return { dateOfBirth, timeOfBirth };
  const parts = splitDT(baby.birthDate || baby.BirthDate ||
    birthEvent.birthDateTime || birthEvent.BirthDateTime);
  return { dateOfBirth: parts.date || dateOfBirth, timeOfBirth: parts.date ? parts.time || '00:00' : timeOfBirth };
};

export const combineLocalDateTimeForSubmit = (date, time) => {
  if (!date) return '';

  const trimmedTime = typeof time === 'string' ? time.trim() : '';
  const normalizedTime = /^\d{2}:\d{2}:\d{2}$/.test(trimmedTime)
    ? trimmedTime
    : /^\d{2}:\d{2}$/.test(trimmedTime)
      ? `${trimmedTime}:00`
      : '00:00:00';

  return `${date}T${normalizedTime}`;
};

export const displayTime = (value) => {
  if (!value || typeof value !== 'string') return '';

  const dateTime = toLocalDateTime(value);
  if (dateTime) return padSeconds(clinicalWallTime(dateTime).slice(11, 19));

  const trimmed = value.trim();
  return /^\d{2}:\d{2}(:\d{2})?$/.test(trimmed) ? padSeconds(trimmed) : '';
};

export const displayDateTime = (value) => {
  if (!value || typeof value !== 'string') return '';

  const dateTime = toLocalDateTime(value);
  if (!dateTime) return value;

  const wall = clinicalWallTime(dateTime);
  const date = new Date(`${wall}Z`);
  if (isNaN(date.getTime())) return dateTime;
  const label = clinicalTimeLabel(dateTime);
  return `${date.toLocaleString([], { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}${label ? ` ${label}` : ''}`;
};

export const formatSummaryTime = (time, is24Hour) => {
  if (!time) return '';

  const [hourString, minute = '00', second = '00'] = String(time).split(':');
  const hour = parseInt(hourString, 10);
  if (isNaN(hour)) return time;

  const clockTime = `${hourString.padStart(2, '0')}:${minute}:${second.slice(0, 2)}`;
  if (is24Hour) return clockTime;

  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minute}:${second.slice(0, 2)} ${period}`;
};

const getBirthSummaryBabyId = (babyEntry) =>
  babyEntry?.referenceId ||
  babyEntry?.ReferenceId ||
  babyEntry?.id ||
  babyEntry?.Id ||
  null;

export const orderTwinBirthReportBabies = (babies = []) =>
  (Array.isArray(babies) ? babies : [])
    .map((babyEntry, index) => ({
      ...babyEntry,
      _summaryOrder:
        Number(
          babyEntry?.twinOrder ??
          babyEntry?.TwinOrder ??
          babyEntry?.birthEvent?.twinOrder ??
          babyEntry?.birthEvent?.TwinOrder
        ) || index + 1
    }))
    .sort((left, right) => left._summaryOrder - right._summaryOrder);

const getBirthEventField = (babyEntry, camelName, pascalName) => {
  const birthEvent = babyEntry?.birthEvent || babyEntry?.BirthEvent || {};
  if (isCriticalTimeUnknown(birthEvent, camelName)) return '';
  return birthEvent[camelName] || birthEvent[pascalName] || '';
};

/**
 * Labor stage starts are shared maternal values, but the Birth Tools record
 * twin pushing/body-born timestamps on each child's birth event. Use the
 * first clinical twin's pushing time for the second-stage fallback and the
 * final clinical twin's body-born time for the third-stage fallback.
 */
export const getTwinLaborStageFallbacks = (babies = []) => {
  const orderedBabies = orderTwinBirthReportBabies(babies);
  const pushingUnknown = orderedBabies.some(entry => isCriticalTimeUnknown(entry.birthEvent || entry.BirthEvent, 'pushingStart'));
  const firstPushingEntry = !pushingUnknown && orderedBabies.find((babyEntry) => (
    getBirthEventField(babyEntry, 'pushingStart', 'PushingStart')
  ));
  const finalBaby = orderedBabies[orderedBabies.length - 1];

  return {
    pushingStart: firstPushingEntry
      ? getBirthEventField(firstPushingEntry, 'pushingStart', 'PushingStart')
      : '',
    bodyBornTime: finalBaby
      ? getBirthEventField(finalBaby, 'bodyBornTime', 'BodyBornTime')
      : ''
  };
};

const parseStageArrayValue = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDateTimeAtOrAfterAnchor = (value, anchor) => {
  if (!value) return '';

  const rawValue = value instanceof Date ? value : String(value).trim();
  if (typeof rawValue === 'string' && clinicalWallTime(rawValue)) return toLocalDateTime(rawValue);
  const isTimeOnly = typeof rawValue === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(rawValue);
  const anchorParts = splitDT(anchor);
  const valueParts = splitDT(rawValue, anchorParts.date);
  if (!valueParts.date || !valueParts.time) return '';

  let date = valueParts.date;
  if (isTimeOnly && anchorParts.date && anchorParts.time && date === anchorParts.date) {
    const valueSeconds = timeToSeconds(valueParts.time);
    const anchorSeconds = timeToSeconds(anchorParts.time);
    if (valueSeconds !== null && anchorSeconds !== null && valueSeconds < anchorSeconds) {
      date = addDaysToDatePart(date, 1);
    }
  }

  return combineLocalDateTimeForSubmit(date, valueParts.time);
};

/**
 * Twin postpartum charting can record two placentas. The end of the third
 * stage is the latest actual placenta-delivery timestamp, not simply the
 * first placenta row or any hemorrhage event that mentions a placenta.
 */
export const getLatestPlacentaDeliveryTime = ({
  postpartumData,
  hemorrhageData,
  birthTimeAnchor
} = {}) => {
  const placentas = parseStageArrayValue(
    postpartumData?.Placentas ?? postpartumData?.placentas
  );
  const hemorrhageEvents = parseStageArrayValue(
    hemorrhageData?.hemorrhageEvents ??
    hemorrhageData?.timelineEvents ??
    hemorrhageData?.TimelineEvents
  );

  const postpartumTimes = placentas.map((placenta) => (
    placenta?.DeliveryTime || placenta?.deliveryTime
  ));
  const hemorrhageTimes = hemorrhageEvents
    .filter((event) => {
      const description = event?.eventDescription || event?.EventDescription || '';
      return String(description).trim().toLowerCase().startsWith('placenta delivered');
    })
    .map((event) => event?.occurredAt || event?.OccurredAt);

  const normalizedTimes = [...postpartumTimes, ...hemorrhageTimes]
    .map((value) => normalizeDateTimeAtOrAfterAnchor(value, birthTimeAnchor))
    .filter(Boolean)
    .sort((a, b) => clinicalSortValue(a) - clinicalSortValue(b));

  return normalizedTimes.length > 0
    ? normalizedTimes[normalizedTimes.length - 1]
    : '';
};

export const getBirthSummaryPresentation = (babyEntry) => {
  const birthEvent = babyEntry?.birthEvent || babyEntry?.BirthEvent || {};
  return (
    birthEvent.birthPresentation ||
    birthEvent.BirthPresentation ||
    babyEntry?.birthPresentation ||
    babyEntry?.BirthPresentation ||
    ''
  );
};

export const buildTwinBirthReportChange = (babyEntry, target = {}, fallbackDate = '') => {
  let value = target.type === 'checkbox' ? target.checked : target.value;
  const criticalTimeFields = new Set([
    'pushingStart',
    'crowning',
    'headBornTime',
    'bodyBornTime',
    'firstBreathTime'
  ]);
  if (criticalTimeFields.has(target.name) && /^\d{2}:\d{2}(:\d{2})?$/.test(value || '')) {
    const birthEvent = babyEntry?.birthEvent || babyEntry?.BirthEvent || {};
    const pascalName = target.name.charAt(0).toUpperCase() + target.name.slice(1);
    const existingFieldDate = splitDT(
      birthEvent[target.name] || birthEvent[pascalName]
    ).date;
    const anchor =
      birthEvent.bodyBornTime ||
      birthEvent.BodyBornTime ||
      birthEvent.birthDateTime ||
      birthEvent.BirthDateTime ||
      babyEntry?.dateOfBirth ||
      babyEntry?.DateOfBirth;
    const anchorParts = splitDT(anchor);
    let eventDate = existingFieldDate || anchorParts.date || fallbackDate;

    if (!existingFieldDate && anchorParts.time && clinicalTimeCandidates(`${eventDate}T${padSeconds(value)}`).length === 1) {
      const editedSeconds = timeToSeconds(padSeconds(value));
      const bodyBornSeconds = timeToSeconds(anchorParts.time);
      if (
        AFTER_BODY_BIRTH_FIELDS.has(target.name) &&
        editedSeconds !== null &&
        bodyBornSeconds !== null &&
        editedSeconds < bodyBornSeconds
      ) {
        eventDate = addDaysToDatePart(eventDate, 1);
      } else if (
        BEFORE_BODY_BIRTH_FIELDS.has(target.name) &&
        editedSeconds !== null &&
        bodyBornSeconds !== null &&
        editedSeconds > bodyBornSeconds
      ) {
        eventDate = addDaysToDatePart(eventDate, -1);
      }
    }

    value = combineLocalDateTimeForSubmit(eventDate, value);
  }

  const eventTarget = { name: target.name, value };
  if (target.type !== undefined) eventTarget.type = target.type;
  if (target.checked !== undefined) eventTarget.checked = target.checked;

  return {
    babyId: getBirthSummaryBabyId(babyEntry),
    event: { target: eventTarget }
  };
};

const parseArrayValue = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getFirstValue = (object, keys) => {
  if (!object) return '';

  for (const key of keys) {
    if (object[key] !== undefined && object[key] !== null && object[key] !== '') {
      return object[key];
    }
  }

  return '';
};

const normalizeExpirationDate = (value) => {
  if (!value) return '';
  return String(value).split('T')[0].split(' ')[0];
};

export const formatMedicationInventoryDetails = (medication = {}) => {
  const lot = getFirstValue(medication, ['lot', 'Lot', 'lotNumber', 'LotNumber']);
  const expirationDate = normalizeExpirationDate(
    getFirstValue(medication, ['expirationDate', 'ExpirationDate', 'expiration', 'Expiration'])
  );

  return [
    lot ? `Lot: ${lot}` : '',
    expirationDate ? `Expires: ${expirationDate}` : ''
  ].filter(Boolean).join(' · ');
};

const combineRecordDateAndTime = (dateValue, timeValue, timeZoneId) => {
  const date = typeof dateValue === 'string' ? dateValue.trim().slice(0, 10) : '';
  const time = typeof timeValue === 'string' ? timeValue.trim() : '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(date) && /^\d{2}:\d{2}(:\d{2})?$/.test(time)) {
    return getLaborRecordChartDateTime({ date, time: padSeconds(time) }, timeZoneId);
  }

  return time || dateValue || '';
};

const HEMORRHAGE_MEDICATION_LABELS = {
  Oxytocin: 'Oxytocin',
  Misoprostol: 'Misoprostol',
  Methylergonovine: 'Methylergonovine',
  Carboprost: 'Carboprost',
  TranexamicAcid: 'Tranexamic Acid',
  Herbs: 'Herbs',
  AdditionalDose: 'Additional Dose'
};

const formatHemorrhageMedicationName = (value) => {
  if (!value) return '';
  return HEMORRHAGE_MEDICATION_LABELS[value] || String(value).replace(/([a-z])([A-Z])/g, '$1 $2');
};

const normalizeClinicalText = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

const administrationTimeKey = (value, zone) => {
  const dateTime = toLocalDateTime(typeof value === 'string' ? value : '');
  if (dateTime) {
    const instant = clinicalInstant(dateTime);
    if (instant !== null) return instant;
    const candidates = clinicalTimeCandidates(dateTime, zone || 'UTC');
    return candidates.length === 1 ? Date.parse(candidates[0]) : null;
  }
  const displayed = displayTime(value);
  return displayed || '';
};

const valuesAreCompatible = (left, right) => {
  const normalizedLeft = normalizeClinicalText(left);
  const normalizedRight = normalizeClinicalText(right);
  return !normalizedLeft || !normalizedRight || normalizedLeft === normalizedRight;
};

const isSameAdministration = (left, right, zone) => (
  normalizeClinicalText(left.name) === normalizeClinicalText(right.name) &&
  administrationTimeKey(left.occurrence || left.time, zone) != null && administrationTimeKey(left.occurrence || left.time, zone) !== '' &&
  administrationTimeKey(left.occurrence || left.time, zone) === administrationTimeKey(right.occurrence || right.time, zone) &&
  valuesAreCompatible(left.dosage, right.dosage) &&
  valuesAreCompatible(left.route, right.route)
);

const mergeMissingMedicationDetails = (existing, incoming) => ({
  ...existing,
  dosage: existing.dosage || incoming.dosage,
  route: existing.route || incoming.route,
  administeredBy: existing.administeredBy || incoming.administeredBy,
  lot: existing.lot || incoming.lot,
  expirationDate: existing.expirationDate || incoming.expirationDate
});

export const buildBirthMedicationEntries = (laborRecords, postpartumData, hemorrhageData, timeZoneId) => {
  const entries = [];

  (laborRecords || []).forEach((record, recordIndex) => {
    const recordDate = getFirstValue(record, ['dateTime', 'DateTime', 'date', 'Date', 'createdAt', 'CreatedAt']);
    const recordTime = getFirstValue(record, ['time', 'Time']);
    parseArrayValue(getFirstValue(record, ['medications', 'Medications'])).forEach((medication, medicationIndex) => {
      const name = getFirstValue(medication, ['name', 'Name', 'medication', 'Medication']);
      if (!name) return;

      entries.push({
        id: `labor-${record.id || record.Id || recordIndex}-${medication.id || medication.Id || medicationIndex}`,
        name,
        dosage: getFirstValue(medication, ['dosage', 'Dosage', 'dose', 'Dose']),
        route: getFirstValue(medication, ['route', 'Route']),
        administeredBy: getFirstValue(medication, ['adminBy', 'AdminBy', 'administeredBy', 'AdministeredBy']),
        lot: getFirstValue(medication, ['lot', 'Lot', 'lotNumber', 'LotNumber']),
        expirationDate: normalizeExpirationDate(getFirstValue(medication, ['expirationDate', 'ExpirationDate', 'expiration', 'Expiration'])),
        time: combineRecordDateAndTime(recordDate, recordTime, timeZoneId),
        occurrence: getLaborRecordOccurrence(record, timeZoneId),
        source: 'Labor Record'
      });
    });
  });

  const postpartumInterventions = parseArrayValue(getFirstValue(postpartumData, ['interventions', 'Interventions']));
  postpartumInterventions.forEach((intervention, index) => {
    const name = getFirstValue(intervention, ['intervention', 'Intervention', 'medication', 'Medication']);
    if (!name) return;

    entries.push({
      id: `postpartum-intervention-${intervention.id || intervention.Id || index}`,
      name,
      dosage: getFirstValue(intervention, ['dose', 'Dose']),
      route: getFirstValue(intervention, ['route', 'Route']),
      administeredBy: getFirstValue(intervention, ['adminBy', 'AdminBy', 'administeredBy', 'AdministeredBy']),
      lot: getFirstValue(intervention, ['lot', 'Lot', 'lotNumber', 'LotNumber']),
      expirationDate: normalizeExpirationDate(getFirstValue(intervention, ['expirationDate', 'ExpirationDate', 'expiration', 'Expiration'])),
      time: getFirstValue(intervention, ['timestamp', 'Timestamp']),
      source: 'Postpartum Assessment'
    });
  });

  const newbornAssessments = parseArrayValue(getFirstValue(postpartumData, ['newbornAssessments', 'NewbornAssessments']));
  newbornAssessments.forEach((assessment, index) => {
    const name = getFirstValue(assessment, ['medication', 'Medication']);
    if (!name || !String(name).trim()) return;

    entries.push({
      id: `postpartum-newborn-${assessment.id || assessment.Id || index}`,
      name,
      dosage: getFirstValue(assessment, ['dose', 'Dose']),
      route: getFirstValue(assessment, ['route', 'Route']),
      administeredBy: getFirstValue(assessment, ['adminBy', 'AdminBy', 'administeredBy', 'AdministeredBy']),
      lot: getFirstValue(assessment, ['lot', 'Lot', 'lotNumber', 'LotNumber']),
      expirationDate: normalizeExpirationDate(getFirstValue(assessment, ['expirationDate', 'ExpirationDate', 'expiration', 'Expiration'])),
      time: getFirstValue(assessment, ['timestamp', 'Timestamp']),
      source: 'Postpartum Newborn Assessment'
    });
  });

  const hemorrhageEvents = parseArrayValue(getFirstValue(hemorrhageData, [
    'hemorrhageEvents',
    'HemorrhageEvents',
    'timelineEvents',
    'TimelineEvents'
  ]));

  hemorrhageEvents.forEach((event, index) => {
    const medicationType = getFirstValue(event, ['medicationType', 'MedicationType', 'medicationName', 'MedicationName']);
    if (!medicationType) return;

    const entry = {
      id: `hemorrhage-${event.id || event.Id || index}`,
      name: formatHemorrhageMedicationName(medicationType),
      dosage: getFirstValue(event, ['dose', 'Dose', 'dosage', 'Dosage']),
      route: getFirstValue(event, ['route', 'Route']),
      administeredBy: getFirstValue(event, ['adminBy', 'AdminBy', 'administeredBy', 'AdministeredBy']),
      lot: getFirstValue(event, ['lot', 'Lot', 'lotNumber', 'LotNumber']),
      expirationDate: normalizeExpirationDate(getFirstValue(event, ['expirationDate', 'ExpirationDate', 'expiration', 'Expiration'])),
      time: getFirstValue(event, ['occurredAt', 'OccurredAt', 'timestamp', 'Timestamp']),
      source: 'Hemorrhage Event'
    };

    const existingIndex = entries.findIndex((existing) => (
      existing.source !== 'Hemorrhage Event' && isSameAdministration(existing, entry, timeZoneId)
    ));

    if (existingIndex >= 0) {
      entries[existingIndex] = mergeMissingMedicationDetails(entries[existingIndex], entry);
    } else {
      entries.push(entry);
    }
  });

  return entries.sort((a, b) => (administrationTimeKey(a.occurrence || a.time, timeZoneId) ?? Infinity) - (administrationTimeKey(b.occurrence || b.time, timeZoneId) ?? Infinity));
};
